import React, { useState, useEffect, useCallback } from "react";
import { getPrompts } from "../../services/prompts";
import { getUsersDropdown } from "../../services/users";
import "./SectionStyles.css";
import toast, { Toaster } from "react-hot-toast";
import { Copy } from "lucide-react";
import LoadingSpinner from "../common/LoadingSpinner";
import EmptyState from "../common/EmptyState";
import { useNavigate } from "react-router-dom";

// Add this helper function before the PromptsView component
function renderStars(rating) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <span
        key={i}
        style={{
          color: i <= rating ? "#fbbf24" : "#e5e7eb",
          fontSize: "1.2em",
          marginRight: 2,
        }}
      >
        ★
      </span>
    );
  }
  return stars;
}

const sourceOptions = [
  { value: "", label: "All Sources" },
  { value: "user_initiated", label: "Used Prompt" },
  { value: "rejected", label: "Rejected Prompt" },
];

// Tag style for both list and popup
const tagBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(90deg, #dbeafe 60%, #e0e7ff 100%)",
  color: "#2563eb",
  padding: "0.18em 0.7em",
  borderRadius: "999px",
  fontWeight: 600,
  fontSize: "0.88em",
  letterSpacing: "0.01em",
  boxShadow: "0 1px 4px rgba(102,126,234,0.07)",
  border: "none",
  marginRight: 0,
  minHeight: "1.7em",
  minWidth: "2.2em",
};

const sourceBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(90deg, #f3f4f6 60%, #e5e7eb 100%)",
  color: "#6b7280",
  padding: "0.18em 0.7em",
  borderRadius: "999px",
  fontWeight: 500,
  fontSize: "0.88em",
  letterSpacing: "0.01em",
  border: "none",
  marginLeft: 0,
  minHeight: "1.7em",
  minWidth: "2.2em",
};

const PromptsView = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [filterCategory, setFilterCategory] = useState("");

  // State for API data and pagination
  const [prompts, setPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // TODO: Populate these from a constants file or API if they are dynamic (similar to PromptsView)
  const tagOptions = [
    { value: "", label: "All Categories" },
    { value: "general", label: "General" },
    { value: "tech", label: "Technical" },
    { value: "creative", label: "Creative Writing" },
    { value: "marketing", label: "Marketing" },
    { value: "education", label: "Education" },
  ];

  // Debounce search term and reset page
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const [viewedPrompt, setViewedPrompt] = useState(null);
  const [filterTag, setFilterTag] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [users, setUsers] = useState([]);

  const navigate = useNavigate();

  // Reset page when filters change
  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  // Debounce search term to prevent API calls on every keystroke
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset page on new search
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    // Fetch users for the "All Users" dropdown
    let mounted = true;
    (async () => {
      try {
        const userList = await getUsersDropdown();
        if (mounted && Array.isArray(userList)) {
          setUsers(userList);
        }
      } catch (e) {
        console.error("Failed to load users for dropdown:", e);
        setUsers([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const fetchPrompts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const queryParamsObject = {
        page: currentPage,
        limit: 20,
      };
      if (debouncedSearchTerm) queryParamsObject.search = debouncedSearchTerm;
      if (filterTag) queryParamsObject.tag = filterTag;
      if (filterUser) queryParamsObject.user = filterUser;
      if (filterSource) queryParamsObject.source = filterSource;

      const responseData = await getPrompts(queryParamsObject);

      setPrompts(responseData.prompts || []);
      setTotalPages(responseData.totalPages || 0);
      setTotalCount(responseData.totalCount || 0);
      // setCurrentPage is already managed by its own state
    } catch (err) {
      setError(err.message);
      toast.error(`Failed to load prompts: ${err.message}`);
      setPrompts([]);
    }
    setIsLoading(false);
  }, [debouncedSearchTerm, filterTag, filterUser, filterSource, currentPage]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // Client-side sorting is still applied to the fetched (and server-filtered) data.
  const sortedPrompts = [...prompts].sort((a, b) => {
    switch (sortBy) {
      case "date":
        return new Date(b.createdAt) - new Date(a.createdAt);
      case "rating":
        return (b.overallRating || 0) - (a.overallRating || 0);
      case "title":
        return (a.promptSearchName || "").localeCompare(
          b.promptSearchName || ""
        );
      default:
        return 0;
    }
  });

  const getRatingText = (rating) => {
    if (!rating) return { text: "Not Rated", color: "#6c757d" };

    const ratingText = rating.toString().toLowerCase();

    switch (ratingText) {
      case "excellent":
        return { text: "Excellent", color: "#28a745" };
      case "good":
        return { text: "Good", color: "#17a2b8" };
      case "average":
        return { text: "Average", color: "#ffc107" };
      case "poor":
        return { text: "Poor", color: "#dc3545" };
      default:
        return { text: rating, color: "#6c757d" };
    }
  };

  const handleUsePrompt = (promptId) => {
    toast.success(`Prompt ${promptId} selected (action to be implemented).`);
    console.log("Using prompt:", promptId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="section-container historical-prompts-section">
      <Toaster position="top-right" />
      <div className="section-content">
        <div
          className="controls-bar card-style"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
            padding: "12px 16px",
          }}
        >
          <div className="search-box filter-item">
            <input
              type="text"
              placeholder="Search prompts (titles, content)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input filter-input"
              style={{
                height: 32,
                fontSize: "0.97em",
                padding: "0 10px",
                minWidth: 180,
                borderRadius: 6,
              }}
            />
          </div>
          <div
            className="filters filter-item"
            style={{ display: "flex", gap: 8 }}
          >
            <select
              value={filterTag}
              onChange={handleFilterChange(setFilterTag)}
              className="form-select filter-select"
              style={{
                height: 32,
                fontSize: "0.97em",
                borderRadius: 6,
                padding: "0 8px",
                minWidth: 110,
              }}
            >
              <option value="">All Tags</option>
              {tagOptions.slice(1).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={filterUser}
              onChange={handleFilterChange(setFilterUser)}
              className="form-select filter-select"
              style={{
                height: 32,
                fontSize: "0.97em",
                borderRadius: 6,
                padding: "0 8px",
                minWidth: 130,
              }}
            >
              <option value="">All Users</option>
              {Array.isArray(users)
                ? users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))
                : null}
            </select>
            <select
              value={filterSource}
              onChange={handleFilterChange(setFilterSource)}
              className="form-select filter-select"
              style={{
                height: 32,
                fontSize: "0.97em",
                borderRadius: 6,
                padding: "0 8px",
                minWidth: 110,
              }}
            >
              {sourceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => {
              setSearchTerm("");
              setFilterTag("");
              setFilterUser("");
              setFilterSource("");
            }}
            style={{
              height: 32,
              fontSize: "0.97em",
              borderRadius: 6,
              padding: "0 16px",
              marginLeft: 8,
              background: "#f3f4f6",
              color: "#374151",
              border: "1px solid #e5e7eb",
              cursor: "pointer",
              transition: "background 0.2s",
              fontWeight: 500,
            }}
          >
            Clear All
          </button>
        </div>

        {isLoading && <LoadingSpinner text="Loading prompts..." />}
        {error && (
          <div
            className="error-message"
            style={{ color: "red", textAlign: "center", padding: "20px" }}
          >
            Error: {error}
          </div>
        )}

        {!isLoading && !error && (
          <div className="prompts-list">
            {sortedPrompts.length === 0 ? (
              <EmptyState
                icon="📝"
                title="No prompts found"
                message="Try adjusting your search or filters, or submit new prompts!"
              />
            ) : (
              sortedPrompts.map((prompt) => (
                <div key={prompt._id} className="prompt-item card-style">
                  <div className="prompt-item-header">
                    <div className="prompt-title-section">
                      <h3>{prompt.promptSearchName || "Untitled Prompt"}</h3>
                      <div className="prompt-badges">
                        {prompt.selectedTag && (
                          <span
                            className="category-badge"
                            style={tagBadgeStyle}
                          >
                            <span
                              style={{
                                marginRight: 5,
                                fontSize: "1em",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              🏷️
                            </span>
                            {prompt.selectedTag}
                          </span>
                        )}
                        <span className="source-badge" style={sourceBadgeStyle}>
                          {prompt.source === "user_initiated" ? (
                            <span
                              style={{
                                marginRight: 5,
                                fontSize: "1em",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              🟢
                            </span>
                          ) : (
                            <span
                              style={{
                                marginRight: 5,
                                fontSize: "1em",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              ⚠️
                            </span>
                          )}
                          {prompt.source === "user_initiated"
                            ? "Used Prompt"
                            : "Rejected Prompt"}
                        </span>
                      </div>
                    </div>
                    <div className="prompt-stats">
                      {(() => {
                        const rating = getRatingText(prompt.overallRating);
                        return (
                          <span
                            className="stat"
                            style={{
                              color: rating.color,
                              fontWeight: "bold",
                            }}
                          >
                            {rating.text}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="prompt-item-content">
                    {/* Use preview card style for each promptTexts key */}
                    <div className="preview-cards-container">
                      {prompt.promptTexts &&
                      Object.values(prompt.promptTexts).some(Boolean) ? (
                        Object.entries(prompt.promptTexts).map(([key, value]) =>
                          value ? (
                            <div key={key} className="preview-card-item">
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <h4
                                  style={{
                                    margin: 0,
                                    fontSize: "1rem",
                                    fontWeight: 500,
                                  }}
                                >
                                  {key.charAt(0).toUpperCase() + key.slice(1)}{" "}
                                  Prompt
                                </h4>
                                <button
                                  className="copy-btn"
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                    marginLeft: "8px",
                                    fontSize: "1.2em",
                                    color: "#667eea",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                  title="Copy prompt text"
                                  onClick={() => {
                                    navigator.clipboard.writeText(value);
                                    toast.success("Prompt text copied!");
                                  }}
                                >
                                  <Copy size={20} />
                                </button>
                              </div>
                              <div className="preview-card-scroll-fade">
                                <p>{value}</p>
                              </div>
                            </div>
                          ) : null
                        )
                      ) : (
                        <div className="preview-card-item">
                          <h4>No prompt text provided.</h4>
                        </div>
                      )}
                    </div>
                    {prompt.userId && typeof prompt.userId === "object" && (
                      <p style={{ fontSize: "0.9em", color: "#555" }}>
                        <strong>User:</strong> {prompt.userId.name || "Unknown"}{" "}
                        ({prompt.userId.email || "No email"})
                      </p>
                    )}
                  </div>

                  <div className="prompt-item-footer">
                    <div className="prompt-dates">
                      <span>
                        Created: {formatDate(prompt.createdAt)}
                        {prompt.userId &&
                          typeof prompt.userId === "object" &&
                          prompt.userId.name && (
                            <>
                              {" by "}
                              <span
                                style={{ fontWeight: 500, color: "#2563eb" }}
                              >
                                {prompt.userId.name}
                              </span>
                            </>
                          )}
                      </span>
                    </div>

                    <div className="prompt-actions">
                      <button
                        onClick={() => {
                          navigate("/?tab=my-prompt", {
                            state: { prefillPrompt: prompt.promptTexts },
                          });
                        }}
                        className="btn btn-primary btn-sm"
                      >
                        Use
                      </button>
                      <button
                        onClick={() => setViewedPrompt(prompt)}
                        className="btn btn-secondary btn-sm"
                        style={{ marginLeft: 8 }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Pagination Controls */}
        <div
          className="pagination-controls"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "2rem",
            gap: "1rem",
          }}
        >
          <button
            className="btn btn-outline"
            onClick={() => setCurrentPage((p) => p - 1)}
            disabled={currentPage <= 1 || isLoading}
          >
            &larr; Previous
          </button>
          <span
            className="pagination-info"
            style={{ color: "#4b5563", fontWeight: 500 }}
          >
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-outline"
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={currentPage >= totalPages || isLoading}
          >
            Next &rarr;
          </button>
        </div>
      </div>

      {/* Modal for viewing prompt details */}
      {viewedPrompt && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{
              maxWidth: 900,
              width: "98vw",
              minHeight: 400,
              maxHeight: "92vh",
              overflowY: "auto",
              position: "relative",
            }}
          >
            <div
              className="modal-header"
              style={{
                justifyContent: "space-between",
                alignItems: "center",
                display: "flex",
                gap: 24,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  flex: 1,
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "1.3rem",
                    fontWeight: 600,
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                >
                  {viewedPrompt.promptSearchName || "Untitled Prompt"}
                </h2>
                <div
                  className="prompt-badges"
                  style={{ marginTop: 10, display: "flex", gap: 10 }}
                >
                  {viewedPrompt.selectedTag && (
                    <span className="category-badge" style={tagBadgeStyle}>
                      <span
                        style={{
                          marginRight: 5,
                          fontSize: "1em",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        🏷️
                      </span>
                      {viewedPrompt.selectedTag}
                    </span>
                  )}
                  <span className="source-badge" style={sourceBadgeStyle}>
                    {viewedPrompt.source === "user_initiated" ? (
                      <span
                        style={{
                          marginRight: 5,
                          fontSize: "1em",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        🟢
                      </span>
                    ) : (
                      <span
                        style={{
                          marginRight: 5,
                          fontSize: "1em",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        ⚠️
                      </span>
                    )}
                    {viewedPrompt.source === "user_initiated"
                      ? "Used Prompt"
                      : "Rejected Prompt"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  navigate("/?tab=my-prompt", {
                    state: { prefillPrompt: viewedPrompt.promptTexts },
                  });
                }}
                className="btn btn-primary btn-sm"
                style={{ marginTop: 4, marginLeft: "auto" }}
              >
                Use
              </button>
              <button
                onClick={() => setViewedPrompt(null)}
                className="btn-close-modal"
                style={{ fontSize: 32, marginLeft: 16, marginTop: 2 }}
                title="Close"
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ padding: 32 }}>
              {/* Ratings and comment at the top */}
              {(viewedPrompt.accuracy ||
                viewedPrompt.usefulness ||
                viewedPrompt.relevancy ||
                viewedPrompt.frequency ||
                viewedPrompt.comment) && (
                <div style={{ marginBottom: 32 }}>
                  <h4
                    style={{
                      marginBottom: 16,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                    }}
                  >
                    Ratings
                    {(() => {
                      const ratingText =
                        viewedPrompt.overallRatingText ||
                        getRatingText(viewedPrompt.overallRating).text;
                      const ratingColor = getRatingText(ratingText).color;
                      return (
                        <span
                          style={{
                            marginLeft: 12,
                            fontWeight: 700,
                            color: ratingColor,
                            fontSize: "1em",
                          }}
                        >
                          {ratingText}
                        </span>
                      );
                    })()}
                  </h4>
                  <div
                    style={{
                      display: "flex",
                      gap: 24,
                      flexWrap: "wrap",
                      marginBottom: viewedPrompt.comment ? 16 : 0,
                    }}
                  >
                    {typeof viewedPrompt.accuracy === "number" && (
                      <div
                        style={{
                          background: "#f8fafc",
                          borderRadius: 8,
                          padding: "14px 18px",
                          minWidth: 140,
                          textAlign: "center",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
                        }}
                      >
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                          Accuracy
                        </div>
                        <div>{renderStars(viewedPrompt.accuracy)}</div>
                        <div
                          style={{
                            fontSize: "0.98em",
                            color: "#888",
                            marginTop: 2,
                          }}
                        >
                          {viewedPrompt.accuracy} / 5
                        </div>
                      </div>
                    )}
                    {typeof viewedPrompt.usefulness === "number" && (
                      <div
                        style={{
                          background: "#f8fafc",
                          borderRadius: 8,
                          padding: "14px 18px",
                          minWidth: 140,
                          textAlign: "center",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
                        }}
                      >
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                          Usefulness
                        </div>
                        <div>{renderStars(viewedPrompt.usefulness)}</div>
                        <div
                          style={{
                            fontSize: "0.98em",
                            color: "#888",
                            marginTop: 2,
                          }}
                        >
                          {viewedPrompt.usefulness} / 5
                        </div>
                      </div>
                    )}
                    {typeof viewedPrompt.relevancy === "number" && (
                      <div
                        style={{
                          background: "#f8fafc",
                          borderRadius: 8,
                          padding: "14px 18px",
                          minWidth: 140,
                          textAlign: "center",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
                        }}
                      >
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                          Relevancy
                        </div>
                        <div>{renderStars(viewedPrompt.relevancy)}</div>
                        <div
                          style={{
                            fontSize: "0.98em",
                            color: "#888",
                            marginTop: 2,
                          }}
                        >
                          {viewedPrompt.relevancy} / 5
                        </div>
                      </div>
                    )}
                    {typeof viewedPrompt.frequency === "number" && (
                      <div
                        style={{
                          background: "#f8fafc",
                          borderRadius: 8,
                          padding: "14px 18px",
                          minWidth: 140,
                          textAlign: "center",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
                        }}
                      >
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                          Frequency
                        </div>
                        <div>{renderStars(viewedPrompt.frequency)}</div>
                        <div
                          style={{
                            fontSize: "0.98em",
                            color: "#888",
                            marginTop: 2,
                          }}
                        >
                          {viewedPrompt.frequency} / 5
                        </div>
                      </div>
                    )}
                  </div>
                  {viewedPrompt.comment && (
                    <div
                      style={{
                        background: "#fffbe6",
                        border: "1px solid #ffe58f",
                        borderRadius: 8,
                        padding: "12px 18px",
                        marginTop: 8,
                        fontStyle: "italic",
                        color: "#ad8b00",
                        fontSize: "1.05em",
                      }}
                    >
                      <strong>Comment:</strong> {viewedPrompt.comment}
                    </div>
                  )}
                </div>
              )}
              <div
                className="preview-cards-container"
                style={{ marginBottom: 24 }}
              >
                {viewedPrompt.promptTexts &&
                Object.values(viewedPrompt.promptTexts).some(Boolean) ? (
                  Object.entries(viewedPrompt.promptTexts).map(([key, value]) =>
                    value ? (
                      <div key={key} className="preview-card-item">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 18,
                          }}
                        >
                          <h4
                            style={{
                              margin: 0,
                              fontSize: "1rem",
                              fontWeight: 500,
                              color: "#374151",
                            }}
                          >
                            {key.charAt(0).toUpperCase() + key.slice(1)} Prompt
                          </h4>
                          <button
                            className="copy-btn"
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                              marginLeft: "8px",
                              fontSize: "1.2em",
                              color: "#667eea",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Copy prompt text"
                            onClick={() => {
                              navigator.clipboard.writeText(value);
                              toast.success("Prompt text copied!");
                            }}
                          >
                            <Copy size={20} />
                          </button>
                        </div>
                        <div className="preview-card-scroll-fade">
                          <p>{value}</p>
                        </div>
                      </div>
                    ) : null
                  )
                ) : (
                  <div className="preview-card-item">
                    <h4>No prompt text provided.</h4>
                  </div>
                )}
              </div>
              {viewedPrompt.userId &&
                typeof viewedPrompt.userId === "object" && (
                  <p style={{ fontSize: "1em", color: "#555" }}>
                    <strong>User:</strong>{" "}
                    {viewedPrompt.userId.name || "Unknown"} (
                    {viewedPrompt.userId.email || "No email"})
                  </p>
                )}
              <div style={{ marginTop: 16, color: "#888", fontSize: "1em" }}>
                <span>Created: {formatDate(viewedPrompt.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptsView;
